const cache = new Map();

function resolveFetchUrl(url) {
  // R2-hosted photos are cross-origin and don't send CORS headers, so a direct
  // fetch() for canvas/base64 embedding would be blocked - route through our
  // backend's same-origin photo-proxy instead. Plain <img> rendering elsewhere
  // in the app is unaffected since that doesn't need CORS.
  if (/^https?:\/\//i.test(url) && !url.startsWith(window.location.origin)) {
    const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
    return `${backendUrl}/api/photo-proxy?url=${encodeURIComponent(url)}`;
  }
  return url;
}

// Some photos were uploaded to storage with an incorrect/generic Content-Type
// (e.g. a PNG stored with a "image/jpeg" header), which makes the browser's
// Blob.type lie about the real format. Embedding an image under the wrong
// declared type produces a broken/blank picture once Excel or a PDF viewer
// tries to decode it. Sniffing the actual magic bytes gives the true format
// (or null for anything that isn't a real image, e.g. a corrupted file).
function sniffImageFormat(bytes) {
  if (bytes.length >= 4 && bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4e && bytes[3] === 0x47) {
    return "png";
  }
  if (bytes.length >= 3 && bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) {
    return "jpeg";
  }
  if (bytes.length >= 4 && bytes[0] === 0x47 && bytes[1] === 0x49 && bytes[2] === 0x46 && bytes[3] === 0x38) {
    return "gif";
  }
  return null;
}

function bytesToBase64(bytes) {
  let binary = "";
  const chunkSize = 8192;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode.apply(null, bytes.subarray(i, i + chunkSize));
  }
  return btoa(binary);
}

export async function loadImageAsDataUrl(url) {
  if (!url) return null;
  if (cache.has(url)) return cache.get(url);
  try {
    const response = await fetch(resolveFetchUrl(url), { credentials: "include" });
    const bytes = new Uint8Array(await response.arrayBuffer());
    const format = sniffImageFormat(bytes);
    if (!format) {
      // Not a real/decodable raster image - skip instead of embedding garbage.
      cache.set(url, null);
      return null;
    }
    const dataUrl = `data:image/${format};base64,${bytesToBase64(bytes)}`;
    cache.set(url, dataUrl);
    return dataUrl;
  } catch {
    cache.set(url, null);
    return null;
  }
}

// Reads the real pixel dimensions of a (data URL) image so callers can fit it
// into a bounding box without stretching/distorting its original aspect ratio.
export function getImageDimensions(dataUrl) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
    img.onerror = () => resolve(null);
    img.src = dataUrl;
  });
}

export async function preloadImages(urls) {
  const unique = [...new Set(urls.filter(Boolean))];
  const entries = await Promise.all(unique.map(async (u) => [u, await loadImageAsDataUrl(u)]));
  return new Map(entries);
}

export function groupByProductId(rows) {
  const groupSize = new Array(rows.length).fill(0);
  let i = 0;
  while (i < rows.length) {
    let j = i;
    while (j < rows.length && rows[j].product_id === rows[i].product_id) j++;
    groupSize[i] = j - i;
    i = j;
  }
  return groupSize;
}
